import React, { useEffect, useState, useRef } from 'react';
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCourseById } from "../../store/courseSlice";
import SideBar from './components/SideBar';
import Overview from './components/Overview';
import QAA from './components/QAA';
import Seminars from './components/Seminars';
import Certification from './components/Certification';
import useWindowWidth from "../../ui/hooks/useWindowWidth";
import { useTranslation } from 'react-i18next';
import { FaExpand, FaCompress } from 'react-icons/fa'; // Используем иконки из react-icons

const PassingCourse = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('Overview');
    const [showVideo, setShowVideo] = useState(false);
    const [video, setVideo] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const videoRef = useRef(null);
    const iframeRef = useRef(null);
    const width = useWindowWidth();
    const handleTabClick = (tab) => setActiveTab(tab);

    const { id } = useParams();
    const dispatch = useDispatch();
    const { course, videoUrl, loading } = useSelector((state) => state.courses);
    const [idTopic, setIdTopic] = useState([]);
    
    useEffect(() => {
        if (id) dispatch(getCourseById(id));
        return () => setShowVideo(false);
    }, [dispatch, id]);

    const toggleFullscreen = () => {
        if (!isFullscreen) {
            if (iframeRef.current.requestFullscreen) {
                iframeRef.current.requestFullscreen();
            } else if (iframeRef.current.webkitRequestFullscreen) {
                iframeRef.current.webkitRequestFullscreen();
            } else if (iframeRef.current.msRequestFullscreen) {
                iframeRef.current.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    const changeVideo = (newUrl) => {
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.src = newUrl;
            videoRef.current.load();
            videoRef.current.play().catch((error) => console.error(t('errors.loading_error'), error));
        }
        setVideo(newUrl);
    };
    
    const changeIdTopic = (newUrl) => {
        setIdTopic(newUrl);
    };
    
    return (
        <div className='pb-[100px]'>
            <div className='lg:flex sm:flex gap-7 ps-10 pe-10'>
                <div className='w-[100%]'>
                    <div className='w-[100%] relative'>
                        {idTopic.video ? (
                            <video 
                                ref={videoRef} 
                                width="100%" 
                                height="auto" 
                                autoPlay 
                                controls 
                                className='w-full object-cover pb-5'
                                style={{
                                    maxHeight: "calc(100vh - 148px)"
                                }}
                            >
                                <source src={video} type="video/mp4" />
                                {t('video_not_supported')}
                            </video>
                        ) : (
                            <div className='flex justify-center bg-black relative'>
                                <iframe 
                                    ref={iframeRef}
                                    src={idTopic.material} 
                                    width="100%" 
                                    className="md:h-[700px] h-[395px]"
                                    title={t('sidebar.material')}
                                >
                                    {t('common.loading')}
                                </iframe>
                                <button 
                                    onClick={toggleFullscreen}
                                    className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded"
                                    aria-label={isFullscreen ? t('buttons.exit_fullscreen') : t('buttons.fullscreen')}
                                >
                                    {isFullscreen ? <FaCompress /> : <FaExpand />}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className='w-[100%] sm:w-[65%]'>
                        <div className='lg:flex sm:flex items-center justify-between py-0 lg:py-2'>
                            <div className='flex justify-around sm:gap-5 items-center mb-3 text-sm sm:text-base shadow-lg sm:shadow-none px-0 sm:px-8 pb-0 sm:pb-0 h-[55px]'>
                                {width < 768 && (
                                    <button
                                        className={`pb-2 ${activeTab === 'Sidebar' ? 'border-b-2 border-black' : 'opacity-80'}`}
                                        onClick={() => handleTabClick('Sidebar')}
                                    >
                                        {t('sidebar.inaugural_lecture')}
                                    </button>
                                )}
                                <button
                                    className={`pb-2 ${activeTab === 'Overview' ? 'border-b-2 border-black' : 'opacity-80'}`}
                                    onClick={() => handleTabClick('Overview')}
                                >
                                    {t('course_info.tabs.course')}
                                </button>
                                <button 
                                    className={`pb-2 ${activeTab === 'QAA' ? 'border-b-2 border-black' : 'opacity-80'}`}
                                    onClick={() => handleTabClick('QAA')}
                                >
                                    {t('buttons.ask_question')}
                                </button>
                            </div>
                            <div className='flex gap-5 justify-end sm:justify-start px-0 lg:px-8 whitespace-nowrap'>
                                <button
                                    className={`px-5 py-1 bg-[#8C1C1C] text-[#FFF] rounded-xl ${activeTab === 'Seminars' ? 'bg-[#402D1D]' : 'bg-[#BA7373]'}`} 
                                    onClick={() => handleTabClick('Seminars')}
                                >
                                    {t('course_info.tabs.sessions')}
                                </button>
                                <button 
                                    className={`px-5 py-1 bg-[#8C1C1C] text-[#FFF] rounded-xl ${activeTab === 'Certification' ? 'bg-[#402D1D]' : 'bg-[#BA7373]'}`} 
                                    onClick={() => handleTabClick('Certification')}
                                >
                                    {t('course_info.tabs.certificate')}
                                </button>
                            </div>
                        </div>

                        <div className='px-0 lg:px-8 pt-5'>
                            {activeTab === 'Overview' && <Overview course={course} />}
                            {activeTab === 'Sidebar' && width < 768 && <SideBar setVideo={changeVideo} setIdTopic={changeIdTopic}/>}
                            {activeTab === 'QAA' && <QAA item={idTopic}/>}
                            {activeTab === 'Seminars' && <Seminars />}
                            {activeTab === 'Certification' && <Certification />}
                        </div>
                    </div>
                </div>
                {
                    width > 768 && (
                        <div className="w-[100%] sm:w-[30%]">
                            <SideBar video={video} setVideo={changeVideo} setIdTopic={changeIdTopic}/>
                        </div>
                    )
                }
            </div>
        </div>
    );
};

export default PassingCourse;